import { useState } from "react";

export default function NewsletterSignup() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setSubmitted(true);
      form.reset();
    } finally {
      setSubmitting(false);
    }
  };

  const topics = [
    { icon: "ri-book-open-line", label: "Financial Education" },
    { icon: "ri-global-line", label: "Diaspora Insights" },
    { icon: "ri-bar-chart-line", label: "Investor Readiness" },
    { icon: "ri-calendar-event-line", label: "Webinars & Events" },
    { icon: "ri-seedling-line", label: "African Opportunity" },
    { icon: "ri-shake-hands-line", label: "Partnership Updates" },
  ];

  return (
    <section className="py-24 px-6 bg-[#1A4D3C] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #C9A961 0, #C9A961 1px, transparent 0, transparent 50%)",
          backgroundSize: "24px 24px",
        }}
      />
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#C9A961]/5 -translate-y-1/2 translate-x-1/2" />

      <div className="relative z-10 max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[#C9A961] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
              <span className="w-8 h-px bg-[#C9A961]" />
              Stay Connected
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Join the Harambee Network
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Subscribe to receive updates on webinars, financial education insights, investor readiness programs, partnership opportunities, and future events. Stay informed and connected to the Harambee Global Ventures community.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {topics.map((t) => (
                <div key={t.label} className="flex items-center gap-2 bg-white/8 rounded-md px-3 py-2.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${t.icon} text-[#C9A961] text-sm`} />
                  </div>
                  <span className="text-white/75 text-xs">{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/8 backdrop-blur-sm rounded-lg p-8 border border-white/15">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#C9A961]/20 mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-[#C9A961] text-2xl" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-2">
                  You&apos;re Subscribed
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">
                  Welcome to the Harambee network. You will receive updates on education, insights, and opportunities directly to your inbox.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-2">
                  Subscribe for Updates
                </h3>
                <p className="text-white/60 text-sm mb-7">
                  Join professionals and partners across the African Diaspora who stay connected through Harambee.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-md border border-white/20 bg-white/10 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-md border border-white/20 bg-white/10 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
                      I am a...
                    </label>
                    <select
                      name="subscriber_type"
                      className="w-full px-4 py-3 rounded-md border border-white/20 bg-[#1A4D3C] text-sm text-white focus:outline-none focus:border-[#C9A961] transition-colors cursor-pointer"
                    >
                      <option value="">Select your profile</option>
                      <option value="Diaspora Professional">Diaspora Professional</option>
                      <option value="Emerging Investor">Emerging Investor</option>
                      <option value="Entrepreneur">Entrepreneur / Founder</option>
                      <option value="Institution or Organization">Institution or Organization</option>
                      <option value="Strategic Partner">Strategic Partner</option>
                      <option value="General Subscriber">General Subscriber</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-md bg-[#C9A961] text-[#1A2B1F] font-semibold text-sm hover:bg-[#b8943f] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {submitting ? "Subscribing..." : "Join the Network"}
                  </button>

                  <p className="text-white/40 text-xs text-center">
                    No spam. Unsubscribe at any time. We respect your privacy.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
